/** Layout context + field mask helpers. See README.md "Field masks" for type safety caveats. */
import { createContext, type ReactNode, useContext } from 'react'

export type FormLayout = 'stacked' | 'stacked-fill' | 'inline' | 'compact' | 'tabular'

export type FieldMask = 'hidden' | 'disabled'
export type FieldMasks = Record<string, FieldMask>

export interface FieldLayoutProps {
  label?: ReactNode
  description?: ReactNode
  control: ReactNode
  error?: ReactNode
  className?: string
  /** Reverse the layout order (control first, then label). Used for e.g. checkboxes where we want the control first. */
  reverseLayout?: boolean
}

// Layout context - just handles layout variant
const FormLayoutContext = createContext<FormLayout>('stacked')

interface FormLayoutProviderProps {
  layout?: FormLayout
  children: ReactNode
}

export function FormLayoutProvider({ layout, children }: FormLayoutProviderProps) {
  const parent = useContext(FormLayoutContext)
  return (
    <FormLayoutContext.Provider value={layout ?? parent}>{children}</FormLayoutContext.Provider>
  )
}

export function useFormLayout(override?: FormLayout) {
  const layoutFromContext = useContext(FormLayoutContext)
  return override ?? layoutFromContext
}

/**
 * Helper to prefix mask keys for nested field groups
 * @example
 * const addressMasks = prefixMasks('address', { line1: 'disabled', line2: 'hidden' })
 * // { 'address.line1': 'disabled', 'address.line2': 'hidden' }
 */
export function prefixMasks(prefix: string, masks?: FieldMasks): FieldMasks | undefined {
  if (!masks) return undefined
  return Object.fromEntries(
    Object.entries(masks).map(([key, value]) => [`${prefix}.${key}`, value])
  )
}

/**
 * Get the mask for a specific field
 */
export function getFieldMask(fieldName: string, masks?: FieldMasks): FieldMask | undefined {
  if (!masks) return undefined
  return masks[fieldName]
}
